import { lazy, Suspense, useId, type ComponentType, type ReactNode, type SVGProps } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { ArrowUpRight } from 'lucide-react';
import { cn } from '@worldcoo/ui/lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@worldcoo/ui/components/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@worldcoo/ui/components/tabs';
import { Skeleton } from '@worldcoo/ui/components/skeleton';
import { DARK_FORM_FIELDS, DiamondGrid, diamondGlows, PillLink } from '../primitives';
import { BuildingIcon, HeartIcon, MicrophoneIcon, PeopleIcon } from './icons';

// Lazy for the same reason the footer does it: the form pulls in zod, the form runtime and the terms
// dialog. Here it also means a visitor who has not yet said who they are downloads none of it.
const ContactForm = lazy(() => import('@/routes/-components/contact-form').then((m) => ({ default: m.ContactForm })));

/**
 * Who is writing. The four the hand-off draws, and deliberately the same four values the shared form
 * takes as its `variant` — so the picker's answer IS the form to render, with no mapping in between.
 */
export type Audience = 'company' | 'ngo' | 'donor' | 'press';

/** The site's white pill, handed to the shared form: the form draws the button, the page styles it. */
type SubmitCta = { label: string; className: string; adornment: ReactNode };

/** Where an NGO goes to start the onboarding no contact form can start. */
// TODO(TEC-3628): pending the real sign-up URL — the footer's own "NGO sign-up" link is still `#` too.
const NGO_SIGNUP_URL = '#';

/** What a donor can answer for themselves: donation status and tax certificate. */
const DONOR_APP_URL = 'https://app.worldcoo.com';

/**
 * The hand-off draws this form at nearly half again the size the shared component ships at — 48px
 * controls against 32px, 16px text against 14px, 16px between fields against 24px. Applied from out
 * here, like the colours in DARK_FORM_FIELDS, so the one form serving `www` and Solutions is untouched.
 *
 * `[&_form]` reaches the <form> the shared component renders, which is where its own gap lives.
 */
const CONTACT_FORM_METRICS = [
	'[&_form]:gap-4',
	'[&_input]:h-12 [&_input]:px-4 [&_input]:text-base',
	'[&_textarea]:px-4 [&_textarea]:py-3 [&_textarea]:text-base',
	'[&_button[role=combobox]]:h-12 [&_button[role=combobox]]:px-4 [&_button[role=combobox]]:text-base',
	// The native select matches, and its chevron moves out with the padding it sits against.
	'[&_select]:h-12 [&_select]:pr-10 [&_select]:pl-4 [&_select]:text-base',
	'[&_[data-slot=native-select-icon]]:right-4',
].join(' ');

/**
 * Sized to the shortest form — name, email, message, consent, button — so picking an audience doesn't
 * make the card jump while the form chunk loads.
 */
function FormSkeleton() {
	return (
		<div className="flex flex-col gap-4">
			{[0, 1].map((i) => (
				<div key={i} className="flex flex-col gap-1">
					<Skeleton className="h-5 w-16 bg-white/10" />
					<Skeleton className="h-12 w-full bg-white/10" />
				</div>
			))}
			<div className="flex flex-col gap-1">
				<Skeleton className="h-5 w-32 bg-white/10" />
				<Skeleton className="h-24 w-full bg-white/10" />
			</div>
			<Skeleton className="h-5 w-64 bg-white/10" />
			<Skeleton className="h-11 w-40 rounded-full bg-white/10" />
		</div>
	);
}

/** The hand-off's confirmation: the panel drops the picker and the form and says only this. */
function SentMessage() {
	const { t } = useLingui();
	return (
		<div className="relative flex flex-1 flex-col justify-center gap-6 py-16 lg:max-w-[440px]">
			<h2 className="text-[32px] leading-[1.3] font-medium tracking-[-0.02em]">{t`Message sent successfully!`}</h2>
			<p className="leading-[1.5] text-white/70">
				{t`Thank you for getting in touch. We have received your message and will reply as soon as possible.`}
			</p>
		</div>
	);
}

/**
 * "Contacta con nosotros" — the dark panel that is the whole point of the Contact page.
 *
 * Two steps, as the hand-off draws them: the visitor first says who they are, on four full-width
 * pills, and only then is asked anything. Once picked, those pills collapse into a select, so the
 * answer stays changeable without four 80px buttons taking up half the card.
 *
 * What follows depends entirely on the audience, and each answer is the shortest one that works: a
 * company gets the full questionnaire, an NGO is pushed towards onboarding rather than a message, a
 * donor is first told they can answer their own question, and the press gets a box and nothing else.
 * A sent message replaces the lot with `SentMessage`. Neither `audience` nor `sent` is state of its
 * own: the left column reacts to both — its logo strip switches on who is writing, its illustration
 * animates when the message goes out — and that column cannot see this one, so the route owns them.
 */
export function ContactPanel({
	audience,
	onAudienceChange,
	sent,
	onSent,
}: {
	audience: Audience | null;
	onAudienceChange: (audience: Audience) => void;
	sent: boolean;
	onSent: () => void;
}) {
	const { t } = useLingui();
	const promptId = useId();

	const audiences: { value: Audience; label: string; icon: ComponentType<SVGProps<SVGSVGElement>> }[] = [
		{ value: 'company', label: t`Company`, icon: BuildingIcon },
		{ value: 'ngo', label: t`NGO / Social entity`, icon: HeartIcon },
		{ value: 'donor', label: t`Donor`, icon: PeopleIcon },
		{ value: 'press', label: t`Press and communication`, icon: MicrophoneIcon },
	];

	// The navbar's CTA, which is the shape every pill on the site takes: 16/10/10 padding around a solid
	// 24px badge. `h-auto` beats the Button variant's own height, which is sized for the backoffice.
	const submit: SubmitCta = {
		label: t`Send message`,
		className:
			'h-auto w-fit gap-2 rounded-full bg-white py-2.5 pr-2.5 pl-4 text-sm font-medium text-black hover:bg-white hover:opacity-90',
		adornment: (
			<span className="bg-ink grid size-6 shrink-0 place-items-center rounded-full text-white">
				<ArrowUpRight className="size-3.5" />
			</span>
		),
	};

	return (
		<section className="bg-ink relative flex flex-col overflow-hidden rounded-3xl px-6 pt-8 pb-8 text-white md:px-12 md:pt-10 md:pb-12 lg:min-h-[801px]">
			{/* One glow, centred at 81.5%/86.7% of the panel — measured off the hand-off's own ellipse. */}
			<DiamondGrid
				color="#fff"
				opacity={0.4}
				size={4.39}
				gap={27.59}
				masks={[diamondGlows({ x: '81.5%', y: '86.7%' })]}
			/>

			{sent ? (
				<SentMessage />
			) : (
				<>
					<h1 className="relative mb-6 text-[32px] leading-[1.3] font-medium tracking-[-0.02em]">
						<Trans>
							Contact <span className="text-olive">us</span>
						</Trans>
					</h1>

					<div className="relative flex flex-col gap-4">
						<p id={promptId} className="text-sm leading-[1.5] font-medium tracking-[-0.02em]">
							{t`Hi! I am…`}
						</p>

						{audience === null ? (
							<div role="group" aria-labelledby={promptId} className="flex flex-col gap-4">
								{audiences.map(({ value, label, icon: Icon }) => (
									<button
										key={value}
										type="button"
										onClick={() => onAudienceChange(value)}
										className="flex h-20 items-center justify-center gap-2 rounded-full border border-white px-4 py-3 font-medium transition-colors hover:bg-white/10"
									>
										<Icon className="size-4 shrink-0 opacity-50" />
										{label}
									</button>
								))}
							</div>
						) : (
							<Select value={audience} onValueChange={(value) => onAudienceChange(value as Audience)}>
								<SelectTrigger
									aria-labelledby={promptId}
									className="w-full rounded-lg border-white/10 bg-white/5 px-4 text-base text-white data-[size=default]:h-12"
								>
									<SelectValue />
								</SelectTrigger>
								<SelectContent className="bg-ink text-white ring-white/10">
									{/* An item's base style paints every descendant with `accent-foreground` on focus, which
									    is a dark colour on a dark row. `!` rather than another rule of the same specificity:
									    which of two equally specific rules wins depends on Tailwind's generated order, not
									    on the order they happen to be written in here. */}
									{audiences.map(({ value, label, icon: Icon }) => (
										<SelectItem key={value} value={value} className="h-12 focus:bg-white/10 focus:**:text-white!">
											<Icon className="size-4 shrink-0 opacity-50" />
											{label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						)}
					</div>

					<div className={cn('relative mt-4', DARK_FORM_FIELDS, CONTACT_FORM_METRICS)}>
						<Suspense fallback={<FormSkeleton />}>
							{audience === 'ngo' ? (
								<NgoBody submit={submit} onSuccess={onSent} />
							) : (
								audience !== null && (
									<>
										{audience === 'donor' && (
											<div className="mb-6 flex flex-col gap-2">
												<h2 className="leading-[1.4] font-medium">
													{t`Do you have questions about how to deduct your donation or how to use your ticket code?`}
												</h2>
												<p className="max-w-[433px] text-sm leading-[1.5] text-white/50">
													<Trans>
														Before writing to us, you can check the status of your donation instantly at{' '}
														<a
															href={DONOR_APP_URL}
															target="_blank"
															rel="noopener noreferrer"
															className="text-olive underline"
														>
															app.worldcoo.com
														</a>{' '}
														and manage your tax certificate yourself.
													</Trans>
												</p>
											</div>
										)}
										<ContactForm variant={audience} submit={submit} onSuccess={onSent} />
									</>
								)
							)}
						</Suspense>
					</div>
				</>
			)}
		</section>
	);
}

/**
 * The NGO fork. An entity that wants to collaborate does not want to write a message — it wants the
 * onboarding form — but one that already collaborates may well have a question, so neither wins: the
 * two sit behind the hand-off's 53px tabs, with collaborating open first.
 */
function NgoBody({ submit, onSuccess }: { submit: SubmitCta; onSuccess: () => void }) {
	const { t } = useLingui();

	const tabs = [
		{ value: 'collaborate', label: t`I want to collaborate` },
		{ value: 'enquire', label: t`I want to ask` },
	];

	return (
		<Tabs defaultValue="collaborate" className="gap-0">
			{/* The list's height and the underline's offset are overridden on the same variant they are
			    set on, so tailwind-merge drops the defaults instead of leaving both in the stylesheet. */}
			<TabsList
				variant="line"
				className="w-full gap-0 border-b border-white/10 p-0 group-data-horizontal/tabs:h-[53px]"
			>
				{tabs.map((tab) => (
					<TabsTrigger
						key={tab.value}
						value={tab.value}
						className="rounded-none text-sm text-white/50 after:bg-white group-data-horizontal/tabs:after:bottom-0 hover:bg-white/5 hover:text-white data-active:bg-transparent data-active:text-white"
					>
						{tab.label}
					</TabsTrigger>
				))}
			</TabsList>

			<TabsContent value="collaborate" className="flex flex-col items-center gap-4 px-6 py-14 text-center">
				<h2 className="leading-[1.4] font-medium">{t`Do you want to start collaborating?`}</h2>
				<p className="max-w-[337px] text-sm leading-[1.5] text-white/50">
					{t`Fill in the form with your entity's basic information to start the sign-up process.`}
				</p>
				<PillLink variant="white" href={NGO_SIGNUP_URL}>
					{t`Go to the form`}
				</PillLink>
			</TabsContent>

			<TabsContent value="enquire" className="px-6 pt-8">
				<ContactForm variant="ngo" submit={submit} onSuccess={onSuccess} />
			</TabsContent>
		</Tabs>
	);
}
